import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { Asset } from '@schibsted-svp/svp-api-types';

import type { AdStartedParam } from '../../../types/global';
import { findClickTrackingUrls, getClickTrackingUrls } from '../utils/vast';

import { usePlayerEvent } from './usePlayerEvent';
import { useJWPlayerEvent } from './useJWPlayerEvent';

export type AdData = AdStartedParam & { clickTrackers: string[] };

export function useAdData(asset?: Asset) {
  const [adData, setAdData] = useState<AdData>();
  const [isAdLoaded, setIsAdLoaded] = useState(false);
  const [isPauseAdVisible, setIsPauseAdVisible] = useState(false);
  const clickTrackers = useRef<
    {
      adId: string;
      creativeId: string;
      clickTrackingUrl: string;
    }[]
  >([]);

  const clearAdData = useCallback(() => {
    setAdData(undefined);
    setIsAdLoaded(false);
    clickTrackers.current = [];
  }, []);

  const setPauseAdVisible = useCallback(() => {
    setIsPauseAdVisible(true);
  }, []);

  const setPauseAdClosed = useCallback(() => {
    setIsPauseAdVisible(false);
  }, []);

  useEffect(() => {
    setIsPauseAdVisible(false);
  }, [asset]);

  const adLoadedXML = useCallback((data: jwplayer.AdLoadedXMLParam) => {
    getClickTrackingUrls(data.xml).forEach((tracker) => {
      if (
        !clickTrackers.current.find(({ adId, creativeId }) => {
          return tracker.adId === adId && tracker.creativeId === creativeId;
        })
      ) {
        clickTrackers.current.push(tracker);
      }
    });
  }, []);

  usePlayerEvent('adSlotComplete', clearAdData);
  useJWPlayerEvent('castAdSlotComplete', clearAdData);
  usePlayerEvent('assetReady', clearAdData);
  useJWPlayerEvent('adError', clearAdData);
  usePlayerEvent('PausePlugin:adVisible', setPauseAdVisible);
  usePlayerEvent('play', setPauseAdClosed);
  usePlayerEvent('PausePlugin:pauseAdClosed', setPauseAdClosed);
  useJWPlayerEvent('adLoadedXML', adLoadedXML);

  const handleAdStarted = useCallback((param: AdStartedParam) => {
    setAdData({
      ...param,
      clickTrackers: findClickTrackingUrls(
        param.meta,
        clickTrackers.current
      ).map(({ clickTrackingUrl }) => clickTrackingUrl),
    });
    setIsAdLoaded(false);
  }, []);

  usePlayerEvent('adStarted', handleAdStarted);

  const handleCastAdData = useCallback((param: jwplayer.CastAdDataParam) => {
    setAdData({
      meta: {
        clickThroughUrl: param.meta.clickThroughUrl,
        duration: param.meta.duration,
        sequence: param.meta.sequence || 1,
        podcount: param.meta.podcount || 1,
      },
      clickTrackers: [],
    });
    setIsAdLoaded(false);
  }, []);

  useJWPlayerEvent('castAdData', handleCastAdData);

  const handleAdLoaded = useCallback(() => {
    setIsAdLoaded(true);
  }, []);

  useJWPlayerEvent('adLoaded', handleAdLoaded);

  return { adData, isAdLoaded, isPauseAdVisible };
}
